import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface FeaturedItem {
  id: string;
  title: string;
  category: string;
  coins: number;
  minutes: string;
  ages: string;
  blurb: string;
  media: string;
}

interface FeaturedProps {
  featured: FeaturedItem[];
}

const categoryColors: Record<string, string> = {
  family: "from-green-500 to-emerald-600",
  thrill: "from-red-500 to-orange-600",
  racing: "from-blue-500 to-cyan-600",
  mind: "from-purple-500 to-pink-600",
};

const Featured: React.FC<FeaturedProps> = ({ featured }) => {
  const [modal, setModal] = useState<FeaturedItem | null>(null);
  
  return (
    <section id="featured" data-section="featured" className="py-12 md:py-16 bg-gradient-to-b from-transparent to-[#0a0a23]/50">
      <div className="max-w-6xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 text-center" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            🌟 Featured Experiences
          </h2>
          <p className="text-center text-white/70 mb-8 text-lg">Try our most popular VR machines and experiences!</p>
        </motion.div>
        
        <div className="flex gap-4 md:gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide">
          {featured.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="min-w-[280px] md:min-w-[320px] max-w-xs bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl overflow-hidden shadow-xl flex-shrink-0 snap-center border border-white/10 hover:border-purple-400/50 transition-all group"
            >
              {/* Image Container */}
              <div className="relative h-48 overflow-hidden bg-gradient-to-br from-purple-900/20 to-blue-900/20">
                <motion.img
                  src={item.media}
                  alt={item.title}
                  loading="lazy"
                  className="w-full h-full object-contain p-4 group-hover:scale-110 transition-transform duration-500"
                  whileHover={{ scale: 1.05 }}
                />
                <div className="absolute top-3 right-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-xs font-bold rounded-full px-3 py-1 shadow-lg">
                  {item.coins} 🪙
                </div>
                {item.category && (
                  <div className={`absolute top-3 left-3 bg-gradient-to-r ${categoryColors[item.category] || 'from-gray-500 to-gray-600'} text-white text-xs font-semibold rounded-full px-3 py-1 shadow-lg capitalize`}>
                    {item.category}
                  </div>
                )}
              </div>
              
              {/* Content */}
              <div className="p-5">
                <h3 className="font-bold text-lg mb-2 text-white" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  {item.title}
                </h3>
                <div className="flex gap-3 text-xs text-white/70 mb-3">
                  <span>⏱️ {item.minutes} min</span>
                  <span>👥 Ages {item.ages}</span>
                </div>
                <p className="text-sm text-white/80 mb-4 line-clamp-2">{item.blurb}</p>
                <motion.button
                  className="w-full px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-semibold shadow-lg hover:shadow-purple-500/50"
                  onClick={() => setModal(item)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  aria-label={`View details for ${item.title}`}
                >
                  View Details
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Modal */}
        <AnimatePresence>
          {modal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
              onClick={() => setModal(null)}
              role="dialog"
              aria-modal="true"
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                className="bg-gradient-to-br from-[#18182f] to-[#0a0a23] rounded-2xl p-6 max-w-lg w-full relative text-white shadow-2xl border border-purple-500/30"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="absolute top-4 right-4 text-white/70 hover:text-white text-3xl font-bold w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
                  onClick={() => setModal(null)}
                  aria-label="Close"
                >
                  ×
                </button>
                
                <div className="relative h-56 rounded-xl overflow-hidden mb-4 bg-gradient-to-br from-purple-900/20 to-blue-900/20">
                  <img
                    src={modal.media}
                    alt={modal.title}
                    loading="lazy"
                    className="w-full h-full object-contain p-4"
                  />
                  <div className="absolute top-3 right-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-sm font-bold rounded-full px-4 py-2 shadow-lg">
                    {modal.coins} 🪙
                  </div>
                </div>
                
                <h3 className="font-bold text-2xl mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  {modal.title}
                </h3>
                
                <div className="flex gap-4 text-sm text-white/70 mb-4">
                  <span className="bg-white/10 rounded-full px-3 py-1">⏱️ {modal.minutes} min</span>
                  <span className="bg-white/10 rounded-full px-3 py-1">👥 Ages {modal.ages}</span>
                  {modal.category && (
                    <span className={`bg-gradient-to-r ${categoryColors[modal.category] || 'from-gray-500 to-gray-600'} rounded-full px-3 py-1 capitalize`}>
                      {modal.category}
                    </span>
                  )}
                </div>
                
                <p className="text-base text-white/90 mb-6 leading-relaxed">{modal.blurb}</p>
                
                <div className="flex gap-3">
                  <motion.a
                    href="/waiver"
                    className="flex-1 px-6 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold text-center shadow-lg hover:shadow-purple-500/50"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    🎮 Play Now
                  </motion.a>
                  <motion.button
                    className="px-6 py-3 rounded-lg bg-white/10 border border-white/20 text-white font-semibold hover:bg-white/20"
                    onClick={() => setModal(null)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Close
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
};

export default Featured;
